import React, { useState } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import { validateFiles } from '../../utils/fileValidation';

/**
 * FileUploadZone - Composant réutilisable pour l'upload de fichiers
 *
 * @param {File[]} files - Liste des fichiers sélectionnés
 * @param {Function} onFilesChange - Callback appelé quand les fichiers changent
 * @param {boolean} disabled - Désactiver l'upload
 * @param {string} acceptedFormats - Formats acceptés (défaut: tous types)
 */
const FileUploadZone = ({
    files = [],
    onFilesChange,
    disabled = false,
    acceptedFormats = ""
}) => {
    // Generate a unique ID for this instance
    const inputId = React.useMemo(() => `file-upload-${Math.random().toString(36).substr(2, 9)}`, []);
    const [error, setError] = useState('');
    const [isDragging, setIsDragging] = useState(false);

    const addFiles = (selectedFiles) => {
        const validation = validateFiles(selectedFiles);
        if (!validation.valid) {
            setError(validation.error);
            return false;
        }
        setError('');
        onFilesChange([...files, ...selectedFiles]);
        return true;
    };

    const handleFileChange = (e) => {
        if (!addFiles(Array.from(e.target.files))) {
            e.target.value = '';
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        addFiles(Array.from(e.dataTransfer.files));
    };

    const removeFile = (indexToRemove) => {
        onFilesChange(files.filter((_, index) => index !== indexToRemove));
    };

    return (
        <div className="space-y-4">
            <div
                onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    disabled ? 'opacity-50 cursor-not-allowed border-white/10' :
                    isDragging ? 'border-yellow-500 bg-yellow-500/10 cursor-copy' : 'border-white/10 hover:border-yellow-500 cursor-pointer'
                }`}>
                <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id={inputId}
                    accept={acceptedFormats}
                    disabled={disabled}
                />
                <label
                    htmlFor={inputId}
                    className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                >
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-300">
                        Glissez-déposez ou cliquez pour sélectionner des fichiers
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                        Tous types de fichiers acceptés (STL, PLY, ZIP, DCM, images, PDF…), 1 Go max
                    </p>
                </label>
            </div>

            {error && (
                <p className="text-sm font-medium text-red-400">{error}</p>
            )}

            {files.length > 0 && (
                <div className="space-y-2">
                    {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between glass border border-white/10 p-3 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <FileText className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-300">{file.name}</span>
                                <span className="text-xs text-gray-500">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-red-400 hover:text-red-300"
                                disabled={disabled}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUploadZone;
